/**
 * Authentication Helper Functions
 * Utilities for handling post-authentication flows
 */

import { firebaseService } from '../services/firebase';
import { hasProjectWorkspaceAccess } from './roles';

/**
 * Determines the appropriate redirect path after authentication
 * - Clients & Admins: directed to their Startup Launchpad Project Workspace (/project)
 * - Regular Signed-Up Users: directed strictly to their Profile (/profile)
 * @param {Object} user - Firebase user object
 * @returns {Promise<string>} - Redirect path
 */
export const getPostAuthRedirect = async (user) => {
  try {
    if (!user) return '/profile';
    const userData = await firebaseService.getUserData(user.uid);
    const combinedUser = { ...user, ...userData };

    if (hasProjectWorkspaceAccess(combinedUser)) {
      return '/project';
    }

    // Regular users land strictly on their profile
    return '/profile';
  } catch (error) {
    console.error('Error determining redirect:', error);
    return '/profile';
  }
};

/**
 * Marks user onboarding as complete
 * @param {string} userId - User ID
 * @returns {Promise<void>}
 */
export const completeOnboarding = async (userId) => {
  try {
    await firebaseService.updateUserProfile(userId, {
      onboardingComplete: true,
      onboardingCompletedAt: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error completing onboarding:', error);
    throw error;
  }
};

/**
 * Checks if user is a new signup (created within last 5 minutes)
 * @param {Object} user - Firebase user object
 * @returns {boolean}
 */
export const isNewUser = (user) => {
  if (!user || !user.metadata) return false;

  const creationTime = new Date(user.metadata.creationTime).getTime();
  const now = Date.now();
  const fiveMinutes = 5 * 60 * 1000;

  return (now - creationTime) < fiveMinutes;
};
